/**
 * Three.js HTML Overlay Scale Utilities
 * 
 * Provides accurate pixel-to-3D unit conversion based on the Soba HTML implementation.
 * These utilities ensure HTML overlays and reflective meshes are sized correctly
 * in 3D space to match the HTML content dimensions.
 * 
 * Based on: https://github.com/angular-threejs/angular-three/blob/main/libs/soba/misc/src/lib/html/utils.ts
 */

import * as THREE from 'three';

/**
 * Reusable vectors to avoid allocations in hot paths
 */
const v1 = new THREE.Vector3();
const v2 = new THREE.Vector3();

/**
 * Calculates the scale factor for an object to maintain pixel-perfect size.
 * 
 * This is the core formula from Soba's HTML implementation that converts
 * between screen pixels and 3D units based on camera FOV and distance.
 * 
 * @param object - The 3D object (or position) to calculate scale for
 * @param camera - The Three.js camera
 * @returns Scale factor (1 / scaleFOV) for pixel-perfect sizing
 * 
 * @example
 * ```typescript
 * const scale = calculateObjectScale(htmlGroup, camera);
 * const width3D = widthPixels * scale * distanceFactor;
 * ```
 */
export function calculateObjectScale(
  object: THREE.Object3D,
  camera: THREE.Camera
): number {
  if (camera.type === 'PerspectiveCamera') {
    const perspCamera = camera as THREE.PerspectiveCamera;
    const objectPos = v1.setFromMatrixPosition(object.matrixWorld);
    const cameraPos = v2.setFromMatrixPosition(camera.matrixWorld);
    const vFOV = (perspCamera.fov * Math.PI) / 180;
    const dist = objectPos.distanceTo(cameraPos);
    const scaleFOV = 2 * Math.tan(vFOV / 2) * dist;
    return 1 / scaleFOV;
  }
  // Orthographic camera uses 1:1 scale
  return 1;
}

/**
 * Calculates 3D dimensions from pixel values for accurate scene sizing.
 * 
 * This is the canonical pixel-to-3D unit conversion function that should be used
 * everywhere you need to convert pixel dimensions to Three.js units. It handles
 * both pixel-perfect mode (distanceFactor=0) and perspective scaling modes.
 * 
 * @param htmlWidthPx - Width in pixels to convert
 * @param htmlHeightPx - Height in pixels to convert
 * @param position - 3D position where the object will be placed (only used for perspective mode)
 * @param camera - The Three.js camera
 * @param viewportSize - Viewport dimensions { width: number, height: number }
 * @param distanceFactor - Scaling mode: 0 = pixel-perfect, >0 = perspective scaling (default: 1.0)
 * @returns [width3D, height3D] dimensions in 3D units
 * 
 * @example
 * ```typescript
 * // Board base dimensions (pixel-perfect mode)
 * const [baseWidth] = calculateHTMLSize(
 *   1000, 1000, // 1000px board
 *   new Vector3(0, 0, 0),
 *   camera,
 *   { width: size.width, height: size.height },
 *   0 // Pixel-perfect mode
 * );
 * const boardWidth = baseWidth * 1.03; // Add 3% margin
 * 
 * // Overlay dimensions (with empirical correction)
 * const adjustedPx = 1000 + Math.ceil(1000 * 0.01); // 1% correction
 * const [overlayWidth] = calculateHTMLSize(
 *   adjustedPx, adjustedPx,
 *   new Vector3(0, 0, 0),
 *   camera,
 *   { width: size.width, height: size.height },
 *   0
 * );
 * ```
 */
export function calculateHTMLSize(
  htmlWidthPx: number,
  htmlHeightPx: number,
  position: THREE.Vector3,
  camera: THREE.Camera,
  viewportSize: { width: number; height: number },
  distanceFactor = 1.0
): [number, number] {
  // SPECIAL CASE: distanceFactor === 0 means pixel-perfect mode (no perspective)
  // This is used by NgtsHTML when you want fixed pixel sizes in transform mode
  // Formula: (pixels / 768) × 9.5 × 2
  // This matches the calibration used across wheel/scratchcard/gameboard
  if (distanceFactor === 0) {
    const width3D = (htmlWidthPx / 768) * 9.5 * 2;
    const height3D = (htmlHeightPx / 768) * 9.5 * 2;
    return [width3D, height3D];
  }
  
  if (camera.type === 'PerspectiveCamera') {
    const perspCamera = camera as THREE.PerspectiveCamera;
    const cameraPos = v2.setFromMatrixPosition(camera.matrixWorld);
    const dist = position.distanceTo(cameraPos);
    const vFOV = (perspCamera.fov * Math.PI) / 180;
    const scaleFOV = 2 * Math.tan(vFOV / 2) * dist;
    
    // Convert pixels to 3D units
    const pixelToUnit = scaleFOV / viewportSize.height;
    const width3D = htmlWidthPx * pixelToUnit * distanceFactor;
    const height3D = htmlHeightPx * pixelToUnit * distanceFactor;
    
    return [width3D, height3D];
  }
  
  // Orthographic camera: pixels map 1:1 to units (simplified)
  // For orthographic, we'd need the camera's zoom and viewport dimensions
  const orthoCamera = camera as THREE.OrthographicCamera;
  const orthoHeight = orthoCamera.top - orthoCamera.bottom;
  const pixelToUnit = orthoHeight / viewportSize.height;
  const width3D = htmlWidthPx * pixelToUnit * distanceFactor;
  const height3D = htmlHeightPx * pixelToUnit * distanceFactor;
  
  return [width3D, height3D];
}

/**
 * Creates a temporary dummy object at a specific position for scale calculations.
 * 
 * Useful when you need to calculate scale at a position but don't have an actual
 * Object3D yet. Creates a temporary group, calculates scale, then returns the value.
 * 
 * @param position - 3D position to calculate scale at
 * @param camera - The Three.js camera
 * @returns Scale factor at that position
 * 
 * @example
 * ```typescript
 * const scale = calculateScaleAtPosition(new Vector3(0, 0, 5), camera);
 * ```
 */
export function calculateScaleAtPosition(
  position: THREE.Vector3,
  camera: THREE.Camera
): number {
  const tempGroup = new THREE.Group();
  tempGroup.position.copy(position);
  tempGroup.updateMatrixWorld(true);
  return calculateObjectScale(tempGroup, camera);
}

/**
 * Interface for overlay configuration options
 */
export interface HTMLOverlayConfig {
  /** Width of HTML content in pixels */
  htmlWidthPx: number;
  /** Height of HTML content in pixels */
  htmlHeightPx: number;
  /** Z position of the HTML overlay (where the HTML plane is) */
  htmlZPosition: number;
  /** How far in front of HTML to place the overlay mesh (e.g., 0.01) */
  overlayOffset?: number;
  /** Distance factor multiplier (default: 1.0) */
  distanceFactor?: number;
  /** Material opacity (default: 0.05) */
  opacity?: number;
  /** Material roughness (default: 0.3) */
  roughness?: number;
  /** Material metalness (default: 0.1) */
  metalness?: number;
  /** Environment map intensity (default: 0.5) */
  envMapIntensity?: number;
}

/**
 * Calculates all properties needed for a reflective HTML overlay mesh.
 * 
 * This is a convenience function that computes position, scale, and
 * material properties for an overlay mesh in one call.
 * 
 * @param config - Overlay configuration options
 * @param camera - The Three.js camera
 * @param viewportSize - Viewport dimensions
 * @returns Object with position, scale, and material properties
 * 
 * @example
 * ```typescript
 * const overlayProps = calculateHTMLOverlayProps({
 *   htmlWidthPx: 460,
 *   htmlHeightPx: 460,
 *   htmlZPosition: 0.11,
 *   overlayOffset: 0.01
 * }, camera, viewportSize);
 * 
 * // Use in template:
 * // [position]="overlayProps.position"
 * // [scale]="overlayProps.scale"
 * ```
 */
export function calculateHTMLOverlayProps(
  config: HTMLOverlayConfig,
  camera: THREE.Camera,
  viewportSize: { width: number; height: number }
) {
  const {
    htmlWidthPx,
    htmlHeightPx,
    htmlZPosition,
    overlayOffset = 0.01,
    distanceFactor = 1.0,
    opacity = 0.05,
    roughness = 0.3,
    metalness = 0.1,
    envMapIntensity = 0.5
  } = config;
  
  // Calculate overlay position (slightly in front of HTML)
  const overlayZ = htmlZPosition + overlayOffset;
  const position = new THREE.Vector3(0, 0, overlayZ);
  
  // Calculate accurate 3D dimensions
  const [width3D, height3D] = calculateHTMLSize(
    htmlWidthPx,
    htmlHeightPx,
    position,
    camera,
    viewportSize,
    distanceFactor
  );
  
  return {
    position: [0, 0, overlayZ] as [number, number, number],
    scale: [width3D, height3D, 1] as [number, number, number],
    material: {
      transparent: true,
      opacity,
      roughness,
      metalness,
      envMapIntensity,
      side: THREE.DoubleSide
    }
  };
}
